import { GAME_LAYOUT, Settings } from "./settings";
import { WILD_SYMBOL_ID, SCATTER_SYMBOL_IDS } from "./SymbolData";

// Dynamically import provider-specific configuration based on RGS_PROVIDER setting
// Uses require.context pattern to avoid hardcoding provider names
const providerConfigContext = require.context(
  "./RGS/Providers/",
  true,
  /^\.\/[^/]+\/providerConfig\.js$/,
);

// Build provider configs map
const providerConfigs = {};
providerConfigContext.keys().forEach((fileName) => {
  const match = fileName.match(/\.\/(.+)\/providerConfig\.js$/);
  if (match) {
    const folderName = match[1];
    providerConfigs[folderName] =
      providerConfigContext(fileName).PROVIDER_CONFIG;
  }
});

// Select the active provider config
const PROVIDER_CONFIG = providerConfigs[Settings.APP.RGS_PROVIDER];

if (!PROVIDER_CONFIG) {
  throw new Error(
    `Provider config not found for: ${Settings.APP.RGS_PROVIDER}. Available providers: ${Object.keys(providerConfigs).join(", ")}`,
  );
}

// Feature configuration
export const FEATURES_CONFIG = {
  DEV_TOOLS: {
    DEBUG_BORDER: false, // If true: shows 1px yellow debug border on key components (DimReelSet, etc.) for measuring alignment
  },
  SPINE_ANIMATIONS: {
    LANDING_ENABLED: true, // Enable landing animations
    IDLE_ENABLED: true, // Enable idle animations
    TURBO_LANDING_ENABLED: false, // If false: turbo mode skips landing/idle animations entirely (shows static textures, zero spine CPU cost)
  },
  DIVIDERS: {
    ENABLED: true, // If false: reel dividers are not rendered or loaded (some games don't use them)
  },
  FREE_SPINS: {
    ENABLED: true,
    ROUNDS: 15, // Number of free spins awarded per trigger
  },
  FREE_ROUNDS: {
    ENABLED: false, // If false: free rounds feature is completely disabled
  },
  TENSION: {
    ENABLED: true, // Enable tension/anticipation mode
    REELSET_ANIMATION: true, // If true: plafys reelset animation (e.g. zoom) during tension
    ENVIRONMENT_ZOOM: true, // If true: background and scene transition containers zoom in sync with reelset
    BACKGROUND_ANIMATION: false, // If true: plays background spine animation during tension
    TRIGGER_SYMBOL: SCATTER_SYMBOL_IDS, // Array of symbol IDs that trigger tension (e.g., SCATTER_SYMBOL_IDS for scatters)
    MIN_SYMBOLS: 2, // Minimum trigger symbols found to trigger anticipation mode (anticipate the 3rd scatter)
    CUTOFF_THRESHOLD: 3, // Stop tension mode when this many trigger symbols are reached (triggers feature round)
    DURATION: 2667, // Fallback duration (ms) — overridden at runtime by spine's actual tension_in + tension_idle + tension_out durations
    POST_TENSION_TRANSITION_BUFFER: 600, // Extra delay (ms) for first reel after tension ends (smooth transition)
    REEL_DECELERATION: {
      ENABLED: false, // If true: tension reels gradually decelerate from full speed to TARGET_SPEED
      DECELERATION_DURATION: 1500, // Duration to decelerate from full → target speed (ms) — balance between gradual and noticeable
      TARGET_SPEED: 0.3, // Final spin speed as fraction of normal (0.3 = 30%) — tweak to taste
      LANDING_DURATION: 900, // Landing animation duration for tension reels (ms) — much longer than normal (375ms) to match slow crawl speed
      BOUNCE_OVERSHOOT: 1.0, // Landing bounce overshoot for tension reels — very gentle to match slow speed
    },
  },

  WIN_LINES: {
    ENABLED: true,
  },
  WIN_BOX: {
    ENABLED: false,
  },
  WIN_LINE_DIGIT: {
    ENABLED: true,
  },
  // Win animation timing configuration
  // All timing values derive from the spine animation loop duration
  WIN_ANIMATION_TIMING: {
    SPINE_LOOP_DURATION: 2000, // Base duration for spine animation full loop (in ms) - used as fallback when spine instance not available
    WIN_LINES_DELAY: 250, // Delay before showing win animations after spin completes (in ms)
    FREE_SPIN_WIN_LINES_DELAY: 400, // Delay before showing win animations in free spins (in ms)
    LOOPS_PER_LINE: 1, // Number of animation loops per winning line (event-based timing)
    get LINE_CYCLE_DURATION() {
      return this.SPINE_LOOP_DURATION; // Fallback duration for line cycling when spine instance not available
    },
  },
  SHOW_ALL_WINS: {
    ENABLED: false, // If true: shows all winning symbols at once with global win text before line cycling
    LOOPS_FOR_SHOW_ALL: 1, // Number of animation loops for "show all wins" phase (event-based timing)
    get DISPLAY_DURATION() {
      return FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION; // 1500ms - matches spine loop (fallback for timeout)
    },
    EXCLUDE_SCATTER: true, // If true: scatter wins skip "show all wins" and go directly to line cycling
  },
  EXPANDING_WILDS: {
    ENABLED: false, // If false: expanding wild feature is completely disabled, win lines start immediately after spin
    MULTIPLIER_ENABLED: false, // If false: expanding wild multiplier feature is disabled (no multiplier sounds or visuals)
    ANIMATION_MODE: "cascade", // "cascade" | "full_reel" | "instant" - cascade: symbol-by-symbol, full_reel: spine overlay, instant: no animation
    CASCADE_OVERLAP_PERCENTAGE: 0.1, // Percentage of "appear" animation duration before triggering next symbol (0.1 = 10% overlap, event-aware timing)
    FULL_REEL_ANIMATION_DURATION: 800, // Total duration for full-reel expanding wild animation in milliseconds.
    // This includes the "in" animation (~300ms) plus idle time (500ms) before win animations begin.
    // The animation plays: "in" (non-looping) → "idle" (looping) → "win" (when win detected)
    SHOW_ALL_WINS_BEFORE_CYCLING: true, // If true: shows all winning symbols after expanding wild animation (overrides SHOW_ALL_WINS.ENABLED)
    // If false: uses SHOW_ALL_WINS.ENABLED setting to determine behavior
    LOOPS_FOR_EXPANDING_WILD_SHOW_ALL: 1, // Number of animation loops for "show all wins" after expanding wild (event-based timing)
    get ALL_WINS_DISPLAY_DURATION() {
      return FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION; // 2000ms - matches spine loop (fallback for timeout)
    },
  },
  TURBO_SUGGESTION: {
    ENABLED: Settings.APP.UI_THEME === "primary", // Only primary theme has turbo suggestion popup
  },
  RESPONSIBLE_GAMBLING: {
    ENABLED: Settings.APP.UI_THEME === "secondary", // Enabled for secondary theme, disabled for primary
  },
  HEARTBEAT: {
    ENABLED: false, // If false: heartbeat manager disabled, no periodic balance sync
    INTERVAL: 30000, // Heartbeat interval in milliseconds (30 seconds)
    START_DELAY: 30000, // Delay before first heartbeat (30 seconds)
    PAUSE_DURING_SPIN: true, // Pause heartbeat during active spins
  },
  BONUS_BUY: {
    ENABLED: true, // If false: bonus buy button and popup completely disabled
  },
  TOOLBAR: {
    TOP: {
      BACKGROUND_MODE: "back", // "front" = toolbar renders its own bg (on top of game), "back" = bg behind game (effects bleed over)
      TOOLBAR_COLOR: "color", // "color" = solid background, "transparent" = no background
    },
    BOTTOM: {
      BACKGROUND_MODE: "front", // "front" = toolbar renders its own bg (on top of game), "back" = bg behind game (effects bleed over)
      TOOLBAR_COLOR: "color", // "color" = solid background, "transparent" = no background
    },
    REELSET_WIN_DISPLAY: {
      BACKGROUND_MODE: "front", // "front" = win display bar renders on top of reelset, "back" = behind reelset
      TOOLBAR_COLOR: "color", // "color" = solid background with rounded corners, "transparent" = no background
    },
  },
  IOS_FULLSCREEN_OVERLAY: {
    ENABLED: false, // If false: iOS Safari fullscreen swipe overlay is completely disabled
  },
  LANGUAGE_SWITCH: {
    ENABLED: false, // If false: language selector in settings is hidden (no localization support yet)
  },
  TURBO: {
    SPIN_SENSITIVITY: 400, // Time to hold button before turbo mode activates (ms)
  },
  SPIN: {
    // Wind-up animation settings
    WIND_UP_DURATION: 55, // Duration of wind-up animation per reel (ms) - set to 0 to disable wind-up
    WIND_UP_SPEED_FACTOR: 0.7, // Multiplier for computed wind-up duration (0.5 = 2x faster, 1.0 = match bounce timing exactly)
    REEL_START_DELAY: 50, // Fallback delay between each reel starting (ms) - used when REEL_START_DELAYS not defined
    WIND_UP_CHECK_BUFFER: 80, // Safety buffer for wind-up completion check (ms) — accounts for full wind-up + release animation

    // Non-linear reel delays — gaps decrease for later reels (natural cascade feel)
    // Inspired by 5 Wild Fire pattern [0,6,12,16,20] where gaps shrink: 6,6,4,4
    REEL_START_DELAYS: [0, 50, 100, 135, 165], // Per-reel start delays (ms) — gaps: 50,50,35,30
    REEL_STOP_DELAYS_AUTO: [0, 375, 750, 1125, 1500], // Auto stop (ms) — gaps >= LANDING_DURATION (375ms) to prevent overlap
    REEL_STOP_DELAYS_MANUAL: [0, 50, 120, 180, 240], // Manual stop (ms) — tighter cascade, gaps: 60,70,60,60

    // Stop delay settings (fallbacks when REEL_STOP_DELAYS not defined)
    STOP_DELAY_MANUAL: 50, // Delay between reels for manual stop (matches REEL_START_DELAY for chronological order)
    STOP_DELAY_AUTO: 250, // Delay between reels for automatic stop (cascade effect)

    // Anti-spam delays for non-winning spins (ms)
    // Prevents button spam by delaying setSpinComplete after reel animations finish
    // Winning spins are protected by counter animation instead
    SPIN_COMPLETE_DELAY: {
      REGULAR: 150, // Delay before setSpinComplete for regular speed
      TURBO: 150, // Delay before setSpinComplete for turbo speed (same as regular for consistency)
    },

    // Auto-play speed mode settings
    AUTO_PLAY_SPEEDS: {
      AUTO: {
        SPIN_DURATION: 700, // Animation loop duration for regular spinning
        SPEED_MS_PER_SYMBOL_HEIGHT: 44, // Milliseconds to travel one symbol height (higher = slower)
        BOUNCE_OVERSHOOT: 1.1, // Phase 1 overshoot — controlled bounce, smooth transition from spin
        STOP_SLOWDOWN_FACTOR: 1.0, // Stop speed relative to spin speed (1.0 = same speed = seamless transition, 0.9 = 90% speed = slower stop)
        AUTO_PLAY_DELAY: 500, // Delay between auto-play spins
        AUTO_STOP_TIMEOUT: 500, // Time before automatic stop starts
        REEL_STOP_SOUND_DELAY: 100, // Delay in milliseconds before playing reel stop sound
        ACCELERATION_DURATION: 200, // Duration of speed ramp-up at spin start (ms) - 0 to disable
        DECELERATION_DURATION: 200, // Duration of deceleration phase before snap-bounce (ms)
        DECELERATION_DISTANCE_RATIO: 0.6, // Fraction of total stop distance covered during deceleration (0.6 = 60%)
        BLUR_VELOCITY_THRESHOLD: 0.6, // Fraction of max speed at which blur activates (0.6 = 60%)
        LANDING_DURATION: 375, // Fixed landing animation duration (ms) — must be <= min gap in REEL_STOP_DELAYS_AUTO
      },
      QUICK: {
        SPIN_DURATION: 90, // Shorter spin duration than AUTO (400ms vs 860ms)
        SPEED_MS_PER_SYMBOL_HEIGHT: 44, // Same as AUTO
        BOUNCE_OVERSHOOT: 1.0, // Same as AUTO
        STOP_SLOWDOWN_FACTOR: 1.0, // Same as AUTO
        AUTO_PLAY_DELAY: 500, // Same as AUTO
        AUTO_STOP_TIMEOUT: 500, // Same as AUTO
        REEL_STOP_SOUND_DELAY: 100, // Same as AUTO
        ACCELERATION_DURATION: 200, // Same as AUTO
        DECELERATION_DURATION: 200, // Same as AUTO
        DECELERATION_DISTANCE_RATIO: 0.6, // Same as AUTO
        BLUR_VELOCITY_THRESHOLD: 0.6, // Same as AUTO
        LANDING_DURATION: 375, // Same as AUTO
        SKIP_WIND_UP: true, // No wind-up animation
        REEL_STOP_DELAYS: [0, 70, 140, 200, 260], // Same as manual stop cascade for snappy feel
      },
      TURBO: {
        // Timing
        SPIN_DELAY: 150,
        AUTO_PLAY_DELAY: 16,
        AUTO_STOP_TIMEOUT: 16,
        MIN_SPIN_DURATION: 5, // Barely moves — almost instant snap to landing
        MIN_SERVER_WAIT: 10,
        REEL_STOP_SOUND_DELAY: 100,
        // Unified spin parameters (ticker-based turbo — same system as normal/quick)
        SPEED_MS_PER_SYMBOL_HEIGHT: 26, // Very fast spin — rapid blur
        ACCELERATION_DURATION: 0, // Instant full speed
        DECELERATION_DURATION: 0, // Straight to snap-bounce
        DECELERATION_DISTANCE_RATIO: 0,
        LANDING_DURATION: 300, // Fast snap-bounce landing
        BOUNCE_OVERSHOOT: 1.0, // Controlled bounce
        STOP_SLOWDOWN_FACTOR: 1.0,
        BLUR_VELOCITY_THRESHOLD: 0.6,
        SKIP_WIND_UP: true, // No wind-up animation
        REEL_START_DELAY: 0,
        REEL_START_DELAYS: [0, 0, 0, 0, 0], // All reels start simultaneously
        REEL_STOP_DELAY: 0,
        REEL_STOP_DELAYS: [0, 0, 0, 0, 0], // All stop at once (when no tension)
        // Legacy
        DROP_DURATION: 350,
      },
    },
  },
  HOLD_AND_WIN: {
    ENABLED: false, // If false: hold and win feature is completely disabled, no intro dialog will be shown
    MAX_WIN_MULTIPLIER: 5000, // Max win threshold multiplier (totalWin >= bet * MAX_WIN_MULTIPLIER) - TODO: Change back to 5000 for production
    INTRO_DELAY: 500, // Delay in ms before showing hold and win intro dialog after reels stop
    COIN_VALUE_FONT_SIZE_MULTIPLIER: 0.24, // Font size for coin values as percentage of symbol height
    // Spin configuration
    SPEED_MS_PER_SYMBOL_HEIGHT: 134, // Milliseconds to move one symbol height (lower = faster)
    BLURRED_TEXTURES_ENABLED: true, // If true: use blurred textures during spin, if false: use regular textures
    // Stop animation configuration
    STOP_SLOWDOWN_FACTOR: 0.33, // Stop speed relative to spin speed (lower = slower stop, 0.35 = ~3x slower than spin)
    BOUNCE_OVERSHOOT: 0.9, // Bounce effect on landing (0 = no bounce, 1.2 = medium bounce, 2.0 = strong bounce)
    // Stop timing configuration
    SPIN_DURATION: 500, // Total spin duration before automatic stop (milliseconds) - needs to be long enough for API response
    SYMBOL_STOP_BASE_DELAY: 50, // Base delay before first symbol stops in cascading (milliseconds) - gives overlay controllers time to initialize
    SYMBOL_STOP_CASCADE_DELAY: 300, // Delay between each symbol stop in cascading for automatic stops (milliseconds)
    SYMBOL_STOP_CASCADE_DELAY_MANUAL: 50, // Manual stop cascade delay for TURBO mode (milliseconds)
    SYMBOL_STOP_CASCADE_DELAY_MANUAL_NORMAL: 25, // Manual stop cascade delay for NORMAL/AUTO speed (milliseconds) - faster for better responsiveness
    // Autoplay configuration
    AUTOPLAY_FIRST_SPIN_DELAY: 800, // Delay after transition completes before first autoplay spin starts in normal mode (milliseconds)
    AUTOPLAY_FIRST_SPIN_DELAY_TURBO: 800, // Delay for turbo mode - quick start after transition (milliseconds)
    AUTOPLAY_SPIN_DELAY: 800, // Delay between autoplay spins in normal mode (milliseconds)
    AUTOPLAY_SPIN_DELAY_TURBO: 625, // Delay between autoplay spins in turbo mode (half of normal speed)
    // Tension mode configuration
    TENSION: {
      ENABLED: true, // Enable tension/anticipation mode for Hold and Win
      COIN_THRESHOLD: 19, // Trigger when this many coins are on reelset (remaining blanks = 20 - COIN_THRESHOLD)
      COIN_GENERATION_PROBABILITY: 0.3, // Probability of generating coins during tension (30% vs 12.5% normal)
      DURATION: 3500, // Duration that symbol spins in tension mode before stopping (milliseconds) - increased for more anticipation
      CASCADE_DELAY: 750, // Delay between each tension symbol stop in cascading (milliseconds)
      TURBO_DURATION: 800, // Shorter tension duration for turbo mode (milliseconds)
      TURBO_CASCADE_DELAY: 150, // Faster cascade delay for turbo tension mode (milliseconds)
    },
    // Turbo mode configuration
    TURBO: {
      SPIN_DURATION: 200, // Minimal spin duration in turbo - drop almost immediately after server response (milliseconds)
      DROP_DURATION: 500, // Drop animation duration - matches regular stop feel (milliseconds)
      BOUNCE_OVERSHOOT: 1.0, // No bounce - lands exactly in place (1.0 = no overshoot)
      SYMBOL_STOP_CASCADE_DELAY: 65, // Cascade delay between symbols in turbo mode (milliseconds)
    },
    // Collection phase configuration
    COLLECTION: {
      TRIGGER_DELAY: 1000, // Delay before starting collection after conditions are met (milliseconds)
      INTRO_ANIMATION_DELAY: 750, // Wait time for collector intro animation (milliseconds)
      SYMBOL_COLLECT_DELAY: 750, // Delay between collecting each symbol (milliseconds)
      MULTIPLIER_UPDATE_DELAY: 50, // Delay after multiplier animation completes to show the multiplied value before fading (milliseconds)
      FINISH_DELAY: 500, // Delay after all symbols collected before finishing (milliseconds)
      OUTRO_DELAY: 1000, // Delay before triggering outro after collection finishes (milliseconds)
      // Counter animation durations
      COUNTER_ANIMATION_PER_COIN: 300, // Counter animation duration per coin in normal mode (milliseconds)
      COUNTER_ANIMATION_SKIP_MODE: 2000, // Counter animation duration in skip mode (milliseconds)
      COUNTER_ANIMATION_MULTIPLIER: 1000, // Counter animation duration for progress bar multiplier (milliseconds)
      ESTIMATED_TIME_PER_COIN: 1600, // Estimated time per coin for continuous counter animation (milliseconds)
    },
  },
  // Win threshold multipliers - loaded from provider-specific configuration
  WIN_THRESHOLDS: PROVIDER_CONFIG.WIN_THRESHOLDS,
  // Win celebration animation configuration
  WIN_CELEBRATION: {
    AUTO_CLOSE_DELAY: 3000, // Auto-close delay after counter finishes (milliseconds) - popup closes automatically if user doesn't interact
  },
  // Max win dialog configuration
  MAX_WIN: {
    ENABLED: false, // When true, max_win animation plays as final tier in win sequence
    AUTO_CLOSE_DELAY: 30000, // Auto-close delay after counter finishes (30 seconds) - dialog closes automatically if user doesn't interact
    MULTIPLIER:
      PROVIDER_CONFIG.WIN_THRESHOLDS.MAX_WIN_THRESHOLD ||
      PROVIDER_CONFIG.MAX_WIN_MULTIPLIER ||
      5000, // Max win cap multiplier (bet × this value) - loaded from provider config
  },
  // Hold and win outro dialog configuration
  HOLD_AND_WIN_OUTRO: {
    AUTO_CLOSE_DELAY: 30000, // Auto-close delay after counter finishes (30 seconds) - dialog closes automatically if user doesn't interact
  },
  // Emergency landing pattern for API rejections (e.g., error 205 free rounds available)
  // Pattern is now generated dynamically via generateNonWinningCombination() utility
  EMERGENCY_LANDING: {
    // Fallback pattern used if dynamic generation fails after max attempts
    // Guaranteed non-winning combination: no coins, no wilds, all different symbols
    FALLBACK_PATTERN: [
      [4, 5, 6, 7], // Reel 0: Regular symbols (no coins, no wilds)
      [5, 6, 7, 8], // Reel 1: Different symbols
      [6, 7, 8, 9], // Reel 2: Different symbols
      [7, 8, 9, 4], // Reel 3: Different symbols
      [8, 9, 4, 5], // Reel 4: Different symbols
    ],
  },
};

// Win type string constants - loaded from provider-specific configuration
export const WIN_TYPES = PROVIDER_CONFIG.WIN_TYPES;

// Animated win types - which wins trigger full-screen celebration
export const ANIMATED_WIN_TYPES = PROVIDER_CONFIG.ANIMATED_WIN_TYPES;

// Centralized mapping of win types to their thresholds (dynamically generated)
export const WIN_TYPE_THRESHOLDS = Object.keys(
  PROVIDER_CONFIG.WIN_TYPES,
).reduce((acc, key) => {
  const thresholdKey = `${key}_THRESHOLD`;
  if (PROVIDER_CONFIG.WIN_THRESHOLDS[thresholdKey]) {
    acc[PROVIDER_CONFIG.WIN_TYPES[key]] =
      PROVIDER_CONFIG.WIN_THRESHOLDS[thresholdKey];
  }
  return acc;
}, {});

// Expanding wild functions - cascading wild animations
/**
 * Build cascade sequences dynamically based on Settings.SYMBOLS_VISIBLE
 * Returns sequences for each possible wild position
 */
export const buildCascadeSequences = () => {
  const visibleCount = GAME_LAYOUT.SYMBOLS_VISIBLE;
  const sequences = {};

  for (let wildPos = 0; wildPos < visibleCount; wildPos++) {
    const sequence = [wildPos]; // Start with wild position

    if (wildPos === 0) {
      // Wild at top → cascade down
      for (let i = 1; i < visibleCount; i++) {
        sequence.push(i);
      }
    } else if (wildPos === visibleCount - 1) {
      // Wild at bottom → cascade up
      for (let i = visibleCount - 2; i >= 0; i--) {
        sequence.push(i);
      }
    } else {
      // Wild in middle → fill one direction first, then the other
      const isLowerHalf = wildPos >= Math.floor(visibleCount / 2);

      if (isLowerHalf) {
        // Fill below first, then above
        for (let i = wildPos + 1; i < visibleCount; i++) {
          sequence.push(i);
        }
        for (let i = wildPos - 1; i >= 0; i--) {
          sequence.push(i);
        }
      } else {
        // Fill above first, then below
        for (let i = wildPos - 1; i >= 0; i--) {
          sequence.push(i);
        }
        for (let i = wildPos + 1; i < visibleCount; i++) {
          sequence.push(i);
        }
      }
    }

    sequences[wildPos] = sequence;
  }

  return sequences;
};

export const EXPANDING_WILD_SEQUENCES = buildCascadeSequences();

/**
 * Detect which middle reels (1, 2, 3) have wild symbols when there's a win
 * Wild should expand if there's a connected winning line adjacent to it
 * @param {Object} currentRoundRef - Ref containing current round data
 * @param {Array} wins - Array of winning combinations from currentRound.wins
 * @returns {Array<{reelIndex: number, wildPosition: number}>} Array of objects with reel index and wild position
 */
export const detectExpandingWilds = (currentRoundRef, wins) => {
  const expandedReels = currentRoundRef.current?.reels;

  if (!expandedReels || !wins || !Array.isArray(wins) || wins.length === 0) {
    return [];
  }

  // Use wild symbol ID from settings (calculated once at module load)
  if (WILD_SYMBOL_ID === null) {
    return [];
  }

  const middleReelIndices = [1, 2, 3];
  const reelsWithWilds = [];

  for (const reelIndex of middleReelIndices) {
    const visibleSymbols = expandedReels[reelIndex];
    if (!visibleSymbols || !Array.isArray(visibleSymbols)) {
      continue;
    }

    // Find FIRST wild position in visible symbols (searching for w1 by symbol ID)
    const wildPosition = visibleSymbols.findIndex(
      (symbolId) => symbolId === WILD_SYMBOL_ID,
    );

    // If wild found, check if it's part of a connected winning line
    if (wildPosition !== -1) {
      // Check if ANY winning combination has this wild reel as part of a connected line
      const isPartOfConnectedWin = wins.some((win) => {
        if (!Array.isArray(win.positions) || win.positions.length === 0) {
          return false;
        }

        // Get all reels involved in this win
        const reelsInWin = win.positions.map(([r]) => r);
        const uniqueReels = [...new Set(reelsInWin)].sort((a, b) => a - b);

        // Check if wild reel is in this win
        if (!uniqueReels.includes(reelIndex)) {
          return false;
        }

        // Find the rightmost connected reel (consecutive from left)
        let rightmostConnected = -1;
        for (let i = 0; i < uniqueReels.length; i++) {
          const currentReel = uniqueReels[i];

          // First reel must be 0 or 1 (wins start from left)
          if (i === 0 && currentReel > 1) {
            break;
          }

          // Check if this reel is consecutive to the previous one
          if (i > 0 && currentReel !== uniqueReels[i - 1] + 1) {
            break; // Gap found, stop here
          }

          rightmostConnected = currentReel;
        }

        // Wild should expand if it's part of the connected sequence (not after a gap)
        const isInConnectedSequence = rightmostConnected >= reelIndex;

        return isInConnectedSequence;
      });

      // Expand wild if it's part of a connected winning line
      if (isPartOfConnectedWin) {
        reelsWithWilds.push({
          reelIndex,
          wildPosition, // This is overlay position (0-3)
        });
      }
    }
  }

  return reelsWithWilds;
};

/**
 * Get the appear animation duration for expanding wilds
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} Appear animation duration in milliseconds (default: 1000ms)
 */
export const getAppearAnimationDuration = (overlayReelController) => {
  // Default fallback: 1000ms
  let appearAnimationDuration = 1000;

  if (overlayReelController) {
    // Use wild symbol ID from settings (calculated once at module load)
    if (WILD_SYMBOL_ID === null) {
      return appearAnimationDuration;
    }

    // Try to get duration from a spine instance
    const spineKey = `0_${WILD_SYMBOL_ID}`; // First position, w1 wild symbol
    const spineInstance =
      overlayReelController.prePositionedSpines?.get(spineKey);

    if (spineInstance?.skeleton?.data?.animations) {
      // Try "appear" first, then "land" as fallback
      let targetAnimation = spineInstance.skeleton.data.animations.find(
        (anim) => anim.name.toLowerCase().includes("appear"),
      );
      if (!targetAnimation) {
        targetAnimation = spineInstance.skeleton.data.animations.find((anim) =>
          anim.name.toLowerCase().includes("land"),
        );
      }
      if (targetAnimation?.duration) {
        appearAnimationDuration = Math.round(targetAnimation.duration * 1000);
      }
    }
  }

  return appearAnimationDuration;
};

/**
 * Calculate the total duration for cascading expanding wild animation
 * @param {number} wildPosition - Position where wild was detected (0-3)
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} Total duration in milliseconds
 */
export const calculateCascadeDuration = (
  wildPosition,
  overlayReelController,
) => {
  // Get the sequence for this wild position
  const sequence =
    EXPANDING_WILD_SEQUENCES[wildPosition] ||
    Array.from({ length: GAME_LAYOUT.SYMBOLS_VISIBLE }, (_, i) => i);

  // Get appear animation duration using helper function
  const appearAnimationDuration = getAppearAnimationDuration(
    overlayReelController,
  );

  // Calculate total cascade time (event-aware timing):
  // - Dynamic delays between symbols: (sequence.length - 1) * (appearDuration * overlap%)
  // - Plus appear animation duration for the last symbol
  const cascadeDelay = Math.round(
    appearAnimationDuration *
      FEATURES_CONFIG.EXPANDING_WILDS.CASCADE_OVERLAP_PERCENTAGE,
  );
  const cascadeDelayTime = (sequence.length - 1) * cascadeDelay;
  const totalDuration = cascadeDelayTime + appearAnimationDuration;

  return totalDuration;
};

/**
 * Play cascading expanding wild animation for a single reel
 * @param {number} reelIndex - Index of the reel to animate
 * @param {number} wildPosition - Position where wild was detected (0-3)
 * @param {Function} dispatch - Redux dispatch function to update state
 */
export const playCascadingExpandingWild = async (
  reelIndex,
  wildPosition,
  reelRefs,
  overlayReelRefs,
  currentTimeoutsRef,
  dispatch,
) => {
  const reels = reelRefs.current;
  const overlayReels = overlayReelRefs.current;

  if (!reels || !overlayReels) {
    return;
  }

  const reelRef = reels[reelIndex];
  const overlayReelRef = overlayReels[reelIndex];

  if (!reelRef || !overlayReelRef) {
    return;
  }

  const reelController = reelRef?.getReelController?.();
  const overlayReelController = overlayReelRef?.getOverlayReelController?.();

  if (!reelController || !overlayReelController) {
    return;
  }

  // Use wild symbol ID from settings (calculated once at module load)
  if (WILD_SYMBOL_ID === null) {
    return;
  }

  // Get the sequence for this wild position
  const sequence =
    EXPANDING_WILD_SEQUENCES[wildPosition] ||
    Array.from({ length: GAME_LAYOUT.SYMBOLS_VISIBLE }, (_, i) => i);

  // Track all symbol replacements for Redux update
  const symbolReplacements = [];

  // Track the last spine instance for event listening
  let lastSpineInstance = null;

  // Calculate cascade delay dynamically based on animation duration (event-aware timing)
  const appearAnimationDuration = getAppearAnimationDuration(
    overlayReelController,
  );
  const cascadeDelay = Math.round(
    appearAnimationDuration *
      FEATURES_CONFIG.EXPANDING_WILDS.CASCADE_OVERLAP_PERCENTAGE,
  );

  // Play animations in sequence with dynamic delays
  for (let i = 0; i < sequence.length; i++) {
    const symbolIndex = sequence[i];

    // Show overlay appear animation at this position (using w1 wild symbol)
    overlayReelController.showAppearAnimationAtPosition(
      symbolIndex,
      WILD_SYMBOL_ID,
    );

    // Update reel texture immediately (no RAF needed in async function)
    reelController.replaceSymbolAtPosition(symbolIndex, WILD_SYMBOL_ID);

    // Track this replacement for Redux update
    symbolReplacements.push({
      position: symbolIndex,
      symbolId: WILD_SYMBOL_ID,
    });

    // Get the spine instance for the last symbol (for event listening)
    if (i === sequence.length - 1) {
      const spineKey = `${symbolIndex}_${WILD_SYMBOL_ID}`;
      lastSpineInstance =
        overlayReelController.prePositionedSpines?.get(spineKey);
    }

    // Wait before next symbol (except for the last one) - event-aware timing
    // IMPORTANT: Do NOT push to currentTimeoutsRef — these are cascade-local delays.
    // Pushing to the shared ref causes reel N's timeout to be cleared when reel M
    // dispatches updateReelSymbols (triggering a re-render that clears the ref).
    if (i < sequence.length - 1) {
      await new Promise((resolve) => {
        setTimeout(resolve, cascadeDelay);
      });
    }
  }

  // Update Redux state with all wild replacements for this reel
  // This ensures next spin reads correct symbols from currentRound.reels
  if (dispatch && symbolReplacements.length > 0) {
    // Import the action dynamically to avoid circular dependency
    const { updateReelSymbols } = await import("./State/actions");
    dispatch(updateReelSymbols({ reelIndex, symbolReplacements }));
  }

  // Wait for the last symbol's animation to complete (event-driven)
  // Resolves on any non-looping animation complete — works for both "appear" and "land" fallback
  if (lastSpineInstance && lastSpineInstance.state) {
    return new Promise((resolve) => {
      const completeListener = {
        complete: () => {
          lastSpineInstance.state.removeListener(completeListener);
          resolve();
        },
      };
      lastSpineInstance.state.addListener(completeListener);
    });
  }
};

/**
 * Get the "in" animation duration for expanding wild full-reel animation
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} "in" animation duration in milliseconds (default: 300ms)
 */
export const getExpandingWildInAnimationDuration = (overlayReelController) => {
  // Default fallback: 300ms (typical "in" animation duration)
  let inAnimationDuration = 300;

  if (overlayReelController?.expandingWildSpine?.skeleton?.data?.animations) {
    const inAnimation =
      overlayReelController.expandingWildSpine.skeleton.data.animations.find(
        (anim) => anim.name === "in",
      );
    if (inAnimation?.duration) {
      inAnimationDuration = Math.round(inAnimation.duration * 1000);
    }
  }

  return inAnimationDuration;
};

/**
 * Play full-reel expanding wild animation for a single reel
 * @param {number} reelIndex - Index of the reel to animate
 * @param {Object} reelRefs - Refs to reel components (for texture replacement)
 * @param {Object} overlayReelRefs - Refs to overlay reel components
 * @returns {Promise} Resolves when "appear" animation completes (event-driven)
 */
export const playFullReelExpandingWild = async (
  reelIndex,
  reelRefs,
  overlayReelRefs,
) => {
  const reels = reelRefs.current;
  const overlayReels = overlayReelRefs.current;

  if (!overlayReels || !reels) {
    return;
  }

  const reelRef = reels[reelIndex];
  const overlayReelRef = overlayReels[reelIndex];

  if (!overlayReelRef || !reelRef) {
    return;
  }

  // Get controllers
  const overlayReelController = overlayReelRef?.getOverlayReelController?.();

  if (!overlayReelController) {
    return;
  }

  // Play the full-reel animation (appear → idle)
  // This will initialize the spine if needed
  overlayReelRef.playExpandingWildFullReel?.();

  // Get the expanding wild spine instance
  const expandingWildSpine = overlayReelController.getExpandingWildSpine?.();

  if (!expandingWildSpine || !expandingWildSpine.state) {
    return;
  }

  // Wait for "appear" animation to complete (event-driven)
  return new Promise((resolve) => {
    const completeListener = {
      complete: (entry) => {
        if (entry.animation.name === "appear") {
          expandingWildSpine.state.removeListener(completeListener);
          resolve();
        }
      },
    };
    expandingWildSpine.state.addListener(completeListener);
  });
};
